/** "How did it go?" on a job card once its order has started: completed, partly done (time left) or not completed. */
import { format, parseISO } from "date-fns";
import React, { useCallback, useEffect, useState } from "react";

import { JobOutcome, JobOutcomeKind } from "../../../../../../services/job-outcomes-api";
import { formatMinutes, parseMinutes } from "../../../../line-activities/utils/line-activities";
import { eyebrowSx, lbb } from "../../utils/tokens";

/** The server stamps outcomes in UTC without a zone; show them in local time. */
const utcStamp = (iso: string) => format(parseISO(iso.endsWith("Z") ? iso : `${iso}Z`), "EEE d MMM HH:mm");

const CHOICES: { kind: JobOutcomeKind; label: string; color: string }[] = [
	{ kind: "completed", label: "Completed", color: lbb.success },
	{ kind: "partial", label: "Partly done", color: lbb.warning },
	{ kind: "notCompleted", label: "Not completed", color: lbb.brick },
];

const inputSx: React.CSSProperties = {
	font: "inherit",
	fontFamily: lbb.fontUi,
	fontSize: 13,
	padding: "5px 8px",
	border: lbb.borderHair,
	borderRadius: 4,
	color: lbb.ink,
	background: lbb.paper,
	boxSizing: "border-box",
};

const buttonSx = (color: string, on: boolean): React.CSSProperties => ({
	border: `1px solid ${on ? color : lbb.navy20}`,
	background: lbb.paper,
	color: on ? color : lbb.navy,
	fontFamily: lbb.fontUi,
	fontSize: 12.5,
	fontWeight: 600,
	borderRadius: 4,
	padding: "4px 10px",
	cursor: "pointer",
	boxShadow: on ? `inset 0 0 0 1px ${color}` : undefined,
});

const Choice: React.FC<{ kind: JobOutcomeKind; label: string; color: string; on: boolean; disabled: boolean; onPick: (kind: JobOutcomeKind) => void }> = ({
	kind,
	label,
	color,
	on,
	disabled,
	onPick,
}) => {
	const pick = useCallback(() => onPick(kind), [onPick, kind]);
	return (
		<button type="button" disabled={disabled} aria-pressed={on} onClick={pick} style={buttonSx(color, on)}>
			{label}
		</button>
	);
};

export const OutcomeSection: React.FC<{
	workOrderId: number;
	outcome?: JobOutcome;
	/** The order has started: only then can what happened be recorded (one already recorded always shows). */
	started: boolean;
	onRecord: (workOrderId: number, outcome: JobOutcomeKind | null, remainingMinutes?: number | null, note?: string | null) => Promise<unknown>;
	onUpdateSchedule: (workOrderId: number) => void;
	onUndo: (moveBatchId: string) => void;
}> = ({ workOrderId, outcome, started, onRecord, onUpdateSchedule, onUndo }) => {
	const [kind, setKind] = useState<JobOutcomeKind | null>(outcome?.outcome ?? null);
	const [left, setLeft] = useState(formatMinutes(outcome?.remainingMinutes));
	const [note, setNote] = useState(outcome?.note ?? "");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		setKind(outcome?.outcome ?? null);
		setLeft(formatMinutes(outcome?.remainingMinutes));
		setNote(outcome?.note ?? "");
	}, [outcome]);

	const minutesLeft = parseMinutes(left);
	const ready = kind !== null && (kind !== "partial" || (typeof minutesLeft === "number" && minutesLeft > 0));
	const changed = kind !== (outcome?.outcome ?? null) || note !== (outcome?.note ?? "") || (kind === "partial" && minutesLeft !== (outcome?.remainingMinutes ?? null));
	const locked = Boolean(outcome?.rescheduledAt);

	const save = useCallback(
		async (next: JobOutcomeKind | null) => {
			setSaving(true);
			setError(null);
			try {
				await onRecord(workOrderId, next, next === "partial" && typeof minutesLeft === "number" ? minutesLeft : null, next ? note : null);
			} catch (err) {
				const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
				setError(message ?? "That was not saved.");
			} finally {
				setSaving(false);
			}
		},
		[onRecord, workOrderId, minutesLeft, note]
	);
	const handleSave = useCallback(() => void save(kind), [save, kind]);
	const handleClear = useCallback(() => void save(null), [save]);
	const handleLeft = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setLeft(event.target.value), []);
	const handleNote = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setNote(event.target.value), []);
	const handleUpdate = useCallback(() => onUpdateSchedule(workOrderId), [onUpdateSchedule, workOrderId]);
	const handleUndo = useCallback(() => outcome?.moveBatchId && onUndo(outcome.moveBatchId), [onUndo, outcome]);

	return (
		<div style={{ borderTop: lbb.borderHair, marginTop: 14, paddingTop: 12 }}>
			<div style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60, marginBottom: 8 }}>How did it go?</div>
			{!started && !outcome && <div style={{ fontFamily: lbb.fontUi, fontSize: 12.5, color: lbb.ink60 }}>You can record what happened once the job has started.</div>}
			{(started || outcome) && (
				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
						{CHOICES.map((choice) => (
							<Choice key={choice.kind} {...choice} on={kind === choice.kind} disabled={locked || saving} onPick={setKind} />
						))}
					</div>
					{kind === "partial" && (
						<label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: lbb.fontUi, fontSize: 12.5, color: lbb.ink }}>
							Time still to run
							<input value={left} onChange={handleLeft} disabled={locked} placeholder="e.g. 2 h" style={{ ...inputSx, width: 90, fontFamily: lbb.fontMono }} />
							<span style={{ color: lbb.ink60 }}>— it continues on the line&apos;s next working time</span>
						</label>
					)}
					{kind === "notCompleted" && (
						<span style={{ fontFamily: lbb.fontUi, fontSize: 12.5, color: lbb.ink60 }}>The batch runs again, next on its line.</span>
					)}
					{kind !== null && (
						<input value={note} onChange={handleNote} disabled={locked} placeholder="Note (what held it up)" style={{ ...inputSx, width: "100%" }} />
					)}
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						{!locked && (
							<button type="button" disabled={!ready || !changed || saving} onClick={handleSave} style={{ ...buttonSx(lbb.navy, true), opacity: !ready || !changed || saving ? 0.5 : 1 }}>
								{saving ? "Saving…" : "Save"}
							</button>
						)}
						{!locked && outcome && (
							<button type="button" disabled={saving} onClick={handleClear} style={{ ...buttonSx(lbb.ink60, false), border: "none" }}>
								Clear
							</button>
						)}
						{!locked && outcome && outcome.outcome !== "completed" && !changed && (
							<button type="button" onClick={handleUpdate} style={{ ...buttonSx(lbb.brick, true), marginLeft: "auto" }}>
								Update schedule…
							</button>
						)}
						{locked && (
							<span style={{ fontFamily: lbb.fontUi, fontSize: 12.5, color: lbb.ink60 }}>
								Schedule updated {outcome?.rescheduledAt ? utcStamp(outcome.rescheduledAt) : ""}.{" "}
								<button type="button" onClick={handleUndo} style={{ ...buttonSx(lbb.brick, false), padding: "2px 8px" }}>
									Undo the update
								</button>
							</span>
						)}
					</div>
					{outcome?.recordedBy && (
						<span style={{ fontFamily: lbb.fontUi, fontSize: 11.5, color: lbb.ink60 }}>
							Recorded by {outcome.recordedBy} · {utcStamp(outcome.recordedAt)}
						</span>
					)}
					{error && <span style={{ fontFamily: lbb.fontUi, fontSize: 12, color: lbb.brickDeep }}>{error}</span>}
				</div>
			)}
		</div>
	);
};
